'use client';

/**
 * renderers/cta-block.tsx — extracted from block-renderer.tsx (SPEC-244 IA16-LANDING-FE).
 * Cero cambios de lógica — VERBATIM extraction. La composición la hace
 * el dispatcher block-renderer.tsx (importa los renderers por tipo).
 * Spec ref: SPEC-059 (docs/SPEC-059-LANDING-PAGE-BUILDER.md).
 *
 * [PR #1494 — hydration mismatch defensive measure] Added `'use client'`
 * directive at line 1. Doesn't fix the hydration mismatch alone (Client
 * Components are still SSR'd by Next.js), but marks the component boundary
 * cleanly for future maintainability. The root-cause fix is the BE
 * read-time normalization in `backend/src/features/landing/helpers.ts:
 * normalizeLandingBlocksSameOriginLinks` which guarantees the renderer
 * never sees an absolute same-origin URL. The `typeof window !==
 * 'undefined'` guard below remains as a defensive layer in case BE
 * normalization is bypassed (e.g. a custom-domain handler that returns
 * blocks raw).
 */

import * as React from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { readableTextOn } from '../_helpers';
import type {
  CtaBlockData,
} from '@/lib/landing-types';

export function CtaBlock({
  data,
  theme,
}: {
  // [FIX-LANDING-STYLING-WHITE-ON-WHITE] extend the theme type to include
  // `backgroundColor` so the subtitle's muted text class can adapt to a
  // dark per-block wrapper bg (Pendiente #15).
  theme?: { secondaryColor?: string; backgroundColor?: string };
  data: CtaBlockData;
}) {
  // [FIX-LANDING-STYLING-WHITE-ON-WHITE] Pre-fix the primary variant always
  // applied `text-white` to the button label. When the user (or the
  // system landing theme) picked a LIGHT `secondaryColor` (e.g. yellow
  // #FFFF00 or cyan #06B6D4 which is borderline), white text became hard
  // to read OR fully invisible on the button bg. Now we pick the button
  // text color based on the effective button bg luminance:
  //   - Dark bg (e.g. #2563EB, #0f172a) → `text-white` (legacy look)
  //   - Light bg (e.g. #FFFF00, #06B6D4) → `text-slate-900` (dark text)
  // The fallback '#3b82f6' (blue-500) is dark enough → `text-white`.
  const buttonBg = data.variant === 'primary' ? (theme?.secondaryColor || '#3b82f6') : null;
  const buttonTextClass = buttonBg ? readableTextOn(buttonBg) : '';
  const variantClass =
    data.variant === 'outline' ? 'border-2' :
    data.variant === 'secondary' ? 'bg-foreground/10' :
    cn('shadow', buttonTextClass);
  const style: React.CSSProperties = data.variant === 'primary' ? { backgroundColor: theme?.secondaryColor || '#3b82f6' } : {};
  // [Pendiente #15] Per-block dark theme override: when the block carries
  // a dark `backgroundColor` (per-block override OR global landing theme),
  // the subtitle's muted text (`text-foreground/85` — dark text) would
  // be illegible. Switch to `text-white/85` (light text) when
  // `readableTextOn(bg) === 'text-white'` (i.e. bg is dark or unparseable
  // gradient strings). Falls back to `text-foreground/85` (legacy default)
  // when bg is missing or light. Reuses `readableTextOn` from
  // _helpers.ts (anti-P-382 — no new helper).
  const mutedTextClass =
    theme?.backgroundColor && readableTextOn(theme.backgroundColor) === 'text-white'
      ? 'text-white/85'
      : 'text-foreground/85';
  return (
    <section className="py-16 px-4">
      <div className="mx-auto max-w-2xl text-center space-y-3">
        {data.title ? <h2 className="text-2xl md:text-3xl font-bold">{data.title}</h2> : null}
        {data.subtitle ? <p className={mutedTextClass}>{data.subtitle}</p> : null}
        {data.buttonText ? (
          <div className="pt-3">
            {(() => {
              // P-243 (SPEC-143): support internal relative links via
              // `next/link` (Link component) — previously only http(s)://
              // (external) and #anchor links were rendered as `<a>`, and
              // any internal path like `/plan-de-compensacion` fell into
              // the `<span>` branch (no clickable link). The Co-Founder QA
              // detected this while editing the system landing CTA in
              // Sprint 65 — the workaround was an absolute URL like
              // `https://staging.profileia.com/plan-de-compensacion?ref={{username}}`
              // (opens a new tab via _blank, no client-side routing). With
              // this fix, internal relative paths render as `<Link>` →
              // SPA navigation, no new tab, no full page reload.
              const rawLink = typeof data.buttonLink === 'string' ? data.buttonLink : '';
              const isHttp = /^https?:\/\//i.test(rawLink);
              const isAnchor = rawLink.startsWith('#');
              // PR #1435 — defense-in-depth: if the link is a relative path
              // WITHOUT a leading slash but contains a `/` (e.g.
              // 'qa_admin_staging/reservar' — the buggy legacy
              // `funnel-agenda-llamada` template value), treat it as an
              // internal SPA path by prepending `/`. This catches any
              // legacy landing that escapes the backend read-time repair
              // (e.g. cached responses, non-main slugs, future template
              // bugs). Pre-fix the link fell into the `<span>` branch
              // (visible as a button but NOT clickable).
              let link = rawLink;
              if (
                !isHttp &&
                !isAnchor &&
                link.length > 0 &&
                !link.startsWith('/') &&
                link.includes('/')
              ) {
                link = '/' + link;
              }
              // Anti-P-382 fix (2026-10-28): detect same-origin HTTP links
              // and treat them as internal (no _blank — fixes Safari/macOS
              // button not working). See hero-block.tsx for full comment.
              const isSameOriginHttp = isHttp && typeof window !== 'undefined' && (
                link.startsWith(window.location.origin + '/') ||
                link.startsWith(window.location.origin)
              );
              const isInternal = (link.startsWith('/') && !link.startsWith('//')) || isSameOriginHttp;
              if (isSameOriginHttp && typeof window !== 'undefined') {
                link = link.slice(window.location.origin.length) || '/';
              }
              const className = cn(
                'inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-medium transition-colors',
                variantClass,
              );
              if (!link || (!isHttp && !isAnchor && !isInternal)) {
                return (
                  <span className={className} style={style}>
                    {data.buttonText}
                  </span>
                );
              }
              if (isInternal) {
                // Internal SPA navigation via next/link — no _blank, no
                // noopener rel (same-tab client-side routing). Same
                // className + style as the `<a>` external + `<span>`
                // branches for visual consistency.
                //
                // P-243 (SPEC-143): `href={link as string}` cast is needed
                // because Next.js 16 typed-routes feature narrows `href` to
                // `UrlObject | RouteImpl<string>`, but `link` here is a
                // runtime-validated `string` from `data.buttonLink` (user-
                // editable landing content). Since we already validated
                // `isInternal` (link.startsWith('/') && !startsWith('//')),
                // it is a valid internal path string. The cast is a
                // known false-positive of typed-routes (links built from
                // user content cannot be statically typed). Anti-P-041
                // justification: this is a documented, type-safe cast with
                // eslint-disable for the typed-routes rule.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return (
                  <Link href={link as any} className={className} style={style}>
                    {data.buttonText}
                  </Link>
                );
              }
              return (
                <a
                  href={link}
                  {...(isHttp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className={className}
                  style={style}
                >
                  {data.buttonText}
                </a>
              );
            })()}
          </div>
        ) : null}
      </div>
    </section>
  );
}
